import { StockDispositionPreview } from "../../components/StockDispositionPreview";
import { useState, useCallback, useEffect, useMemo, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Modal } from "@/components/base/Modal";
import { StatusBadge } from "@/components/base/StatusBadge";
import { BulkActionBar } from "@/components/feature/BulkActionBar";
import { useBulkSelection } from "@/components/feature/useBulkSelection";
import { money, variantLabel } from "@/lib/format";
import * as productApi from "@/lib/api/products";
import { retireSelectedVariant } from "@/lib/api/listing-sync";
import { cleanupCatalogueMediaAssets } from "@/lib/media";
import { buildCanonicalVariantSku, canonicalSkuBase, effectiveSellingPrice, isVariantPriceOverride, effectiveCostPrice, isVariantCostOverride, normalizeSku, parseOptionalNumericInput, skuValidationError, uniqueSku } from "@/lib/products";
import VariantMatrixModalV2 from "./VariantMatrixModalV2";
import { inputCls, labelCls, type ArchiveReadiness, type DeleteSafety, type ProductRow, type VariantRow } from "../types";

// Self-contained "Variants & SKUs" feature: the variant table (Variant, SKU,
// Barcode, Price, Cost, Stock, Status) with search/status filters, pagination,
// bulk status actions, plus add/edit, bulk matrix generation, and archive/
// restore/delete lifecycle. Owns all its own state and mutations.

interface Props {
  productId: string;
  orgId: string;
  product: ProductRow;
  variants: VariantRow[];
  stockByVariant?: Record<string, { available: number; onHand: number; allocated: number }>;
  onEditPrices?: () => void;
}

export default function VariantsTab({ productId, orgId, product, variants, stockByVariant, onEditPrices }: Props) {
  const queryClient = useQueryClient();
  const [variantEditingId, setVariantEditingId] = useState<string | null>(null);
  const [variantSaving, setVariantSaving] = useState(false);
  const [variantError, setVariantError] = useState("");
  const [variantDeleting, setVariantDeleting] = useState<string | null>(null);
  const [variantOpen, setVariantOpen] = useState(false);
  const [variantForm, setVariantForm] = useState({ sku: "", barcode: "", option_name: "", option_value: "", cost_price: "", selling_price: "", weight_kg: "", length_cm: "", width_cm: "", height_cm: "" });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [variantSearch, setVariantSearch] = useState("");
  const [variantStatus, setVariantStatus] = useState("all");
  const [variantMissingSku, setVariantMissingSku] = useState(false);
  const [variantMissingBarcode, setVariantMissingBarcode] = useState(false);
  const [variantPage, setVariantPage] = useState(0);
  const [variantDeleteId, setVariantDeleteId] = useState<string | null>(null);
  const [variantDeleteLoading, setVariantDeleteLoading] = useState(false);
  const [variantDeleteSafety, setVariantDeleteSafety] = useState<DeleteSafety | null>(null);
  const [variantDeleteError, setVariantDeleteError] = useState("");
  const [variantDeleteBusy, setVariantDeleteBusy] = useState(false);
  const [variantWriteOffStock, setVariantWriteOffStock] = useState(false);
  const [variantArchiveId, setVariantArchiveId] = useState<string | null>(null);
  const [variantArchiveLoading, setVariantArchiveLoading] = useState(false);
  const [variantArchiveReadiness, setVariantArchiveReadiness] = useState<ArchiveReadiness | null>(null);
  const [variantArchiveError, setVariantArchiveError] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkError, setBulkError] = useState("");
  const [skuStandardOpen, setSkuStandardOpen] = useState(false);
  const [skuStandardBase, setSkuStandardBase] = useState("");
  const [skuStandardBusy, setSkuStandardBusy] = useState(false);
  const [skuStandardError, setSkuStandardError] = useState("");
  const [skuStandardSuccess, setSkuStandardSuccess] = useState("");

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["product-detail", orgId, productId] });

  const openAddVariant = () => {
    setVariantEditingId(null);
    setVariantForm({ sku: "", barcode: "", option_name: "", option_value: "", cost_price: "", selling_price: "", weight_kg: "", length_cm: "", width_cm: "", height_cm: "" });
    setVariantError("");
    setVariantOpen(true);
  };

  const openEditVariant = (v: VariantRow) => {
    const entries = Object.entries(v.option_values ?? {});
    setVariantEditingId(v.id);
    setVariantForm({
      sku: v.sku ?? "",
      barcode: v.barcode ?? "",
      option_name: entries.map(([k, val]) => `${k}:${val}`).join("|"),
      option_value: "",
      cost_price: v.cost_price != null ? String(v.cost_price) : "",
      selling_price: v.selling_price != null ? String(v.selling_price) : "",
      weight_kg: v.weight_kg != null ? String(v.weight_kg) : "",
      length_cm: v.length_cm != null ? String(v.length_cm) : "",
      width_cm: v.width_cm != null ? String(v.width_cm) : "",
      height_cm: v.height_cm != null ? String(v.height_cm) : "",
    });
    setVariantError("");
    setVariantOpen(true);
  };

  const saveVariant = async (e: FormEvent) => {
    e.preventDefault();
    setVariantError("");
    setVariantSaving(true);
    const normalizedVariantSku = normalizeSku(variantForm.sku);
    const variantSkuError = skuValidationError(normalizedVariantSku);
    if (variantSkuError) {
      setVariantSaving(false);
      setVariantError(variantSkuError);
      return;
    }
    const optionValues: Record<string, string> = {};
    if (variantForm.option_name.trim()) {
      const pairs = variantForm.option_name.split("|");
      for (const pair of pairs) {
        const [name, value] = pair.split(":");
        if (name?.trim() && value?.trim()) {
          optionValues[name.trim()] = value.trim();
        }
      }
    }
    try {
      await productApi.saveProductVariant({
        orgId,
        productId,
        variantId: variantEditingId,
        sku: normalizedVariantSku,
        barcode: variantForm.barcode.trim() || null,
        cost_price: parseOptionalNumericInput(variantForm.cost_price),
        selling_price: parseOptionalNumericInput(variantForm.selling_price),
        weight_kg: parseOptionalNumericInput(variantForm.weight_kg),
        length_cm: parseOptionalNumericInput(variantForm.length_cm),
        width_cm: parseOptionalNumericInput(variantForm.width_cm),
        height_cm: parseOptionalNumericInput(variantForm.height_cm),
        optionValues,
        position: variants.length,
      });
    } catch (e) {
      setVariantSaving(false);
      setVariantError(e instanceof Error ? e.message : String(e));
      return;
    }
    setVariantSaving(false);
    setVariantOpen(false);
    invalidate();
  };

  const generateSku = useCallback(() => {
    const base = canonicalSkuBase(product.reference ?? product.sku ?? product.name);
    const taken = new Set(
      variants.map((v) => normalizeSku(v.sku ?? "")).filter(Boolean),
    );
    return uniqueSku(base, taken);
  }, [product, variants]);

  const standardizableVariants = useMemo(
    () => variants.filter((v) => (v.status ?? "active") !== "archived"),
    [variants],
  );

  const skuStandardPreview = useMemo(() => {
    const base = normalizeSku(skuStandardBase);
    if (!base || skuValidationError(base)) return [];
    const taken = new Set<string>();
    return standardizableVariants.map((variant) => ({
      variant,
      oldSku: variant.sku ?? "",
      newSku: buildCanonicalVariantSku(base, variant.option_values, taken),
    }));
  }, [skuStandardBase, standardizableVariants]);

  const openSkuStandardizer = () => {
    setSkuStandardBase(canonicalSkuBase(product.reference ?? product.sku ?? product.name));
    setSkuStandardError("");
    setSkuStandardSuccess("");
    setSkuStandardOpen(true);
  };

  const applySkuStandardization = async () => {
    setSkuStandardError("");
    setSkuStandardSuccess("");
    const base = normalizeSku(skuStandardBase);
    const baseError = skuValidationError(base);
    if (baseError) {
      setSkuStandardError(baseError);
      return;
    }
    const changes = skuStandardPreview
      .filter((row) => normalizeSku(row.oldSku) !== row.newSku)
      .map((row) => ({
        variant_id: row.variant.id,
        expected_old_sku: row.oldSku,
        new_sku: row.newSku,
      }));
    if (changes.length === 0) {
      setSkuStandardSuccess("All active variants already follow this SKU pattern.");
      return;
    }

    setSkuStandardBusy(true);
    try {
      const result = await productApi.standardizeProductVariantSkus(orgId, productId, base, changes);
      if (!result.success) {
        const blockerText = result.blockers?.map((b) => b.message).join(" ") || result.code || "SKU standardisation was blocked.";
        setSkuStandardError(blockerText);
      } else {
        setSkuStandardSuccess(`Updated ${result.updated ?? changes.length} SKU${(result.updated ?? changes.length) === 1 ? "" : "s"}. Marketplace approvals may now require review.`);
        await invalidate();
      }
    } catch (e) {
      setSkuStandardError(e instanceof Error ? e.message : String(e));
    } finally {
      setSkuStandardBusy(false);
    }
  };

  const openVariantArchiveConfirm = async (variantId: string) => {
    setVariantArchiveError("");
    setVariantArchiveReadiness(null);
    setVariantArchiveId(variantId);
    setVariantArchiveLoading(true);
    try {
      setVariantArchiveReadiness(await productApi.checkVariantArchiveReadiness(orgId, variantId));
    } catch (e) {
      setVariantArchiveError((e as Error).message);
    }
    setVariantArchiveLoading(false);
  };

  const archiveVariant = async (variantId: string) => {
    setVariantDeleting(variantId);
    try {
      await productApi.setVariantStatus(orgId, variantId, "archived");
    } catch (e) {
      setVariantDeleting(null);
      setVariantError((e as Error).message);
      return;
    }
    setVariantDeleting(null);
    setVariantArchiveId(null);
    setVariantArchiveReadiness(null);
    invalidate();
  };

  const restoreVariant = async (variantId: string) => {
    setVariantDeleting(variantId);
    try {
      await productApi.setVariantStatus(orgId, variantId, "active");
    } catch (e) {
      setVariantDeleting(null);
      setVariantError((e as Error).message);
      return;
    }
    setVariantDeleting(null);
    invalidate();
  };

  const openVariantDeleteConfirm = async (variantId: string) => {
    setVariantDeleteError("");
    setVariantDeleteSafety(null);
    setVariantWriteOffStock(false);
    setVariantDeleteId(variantId);
    setVariantDeleteLoading(true);
    try {
      setVariantDeleteSafety(await productApi.checkVariantDeleteSafety(orgId, variantId));
    } catch (e) {
      setVariantDeleteError((e as Error).message);
    }
    setVariantDeleteLoading(false);
  };

  const handleVariantDelete = async () => {
    if (!variantDeleteId) return;
    setVariantDeleteBusy(true);
    setVariantDeleteError("");
    try {
      let safety = variantDeleteSafety;
      if (safety?.blockers.some((block) => block.code === "remote_listings")) {
        const remote = await retireSelectedVariant({ orgId, variantId: variantDeleteId });
        if (!remote.success) {
          throw new Error(remote.error || "Selected-variant marketplace retirement needs reconciliation.");
        }
        safety = await productApi.checkVariantDeleteSafety(orgId, variantDeleteId);
        setVariantDeleteSafety(safety);
        if (!safety.can_delete && safety.blockers.some((block) => block.code === "remote_listings")) {
          throw new Error("Marketplace retirement is not confirmed for every live listing.");
        }
      }
      const result = await productApi.deleteVariantPermanently(orgId, variantDeleteId, variantWriteOffStock, safety?.stock_confirmation ?? null);
      if (result.cleanup_media_asset_ids.length > 0) {
        await cleanupCatalogueMediaAssets(orgId, result.cleanup_media_asset_ids).catch(() => undefined);
      }
    } catch (e) {
      setVariantDeleteBusy(false);
      setVariantDeleteError((e as Error).message);
      setVariantWriteOffStock(false);
      try { setVariantDeleteSafety(await productApi.checkVariantDeleteSafety(orgId, variantDeleteId)); }
      catch { setVariantDeleteSafety(null); }
      return;
    }
    setVariantDeleteBusy(false);
    setVariantDeleteId(null);
    setVariantDeleteSafety(null);
    setVariantWriteOffStock(false);
    invalidate();
  };

  const PAGE_SIZE = 50;
  const filteredVariants = variants.filter((v) => {
    if (variantStatus !== "all" && (v.status ?? "active") !== variantStatus) return false;
    if (variantMissingSku && (v.sku ?? "").trim() !== "") return false;
    if (variantMissingBarcode && (v.barcode ?? "").trim() !== "") return false;
    if (!variantSearch.trim()) return true;
    const q = variantSearch.toLowerCase();
    return (
      (v.sku ?? "").toLowerCase().includes(q) ||
      (v.barcode ?? "").toLowerCase().includes(q) ||
      Object.entries(v.option_values ?? {}).some(([k, val]) => k.toLowerCase().includes(q) || val.toLowerCase().includes(q))
    );
  });
  const totalVariantPages = Math.max(1, Math.ceil(filteredVariants.length / PAGE_SIZE));
  const safeVariantPage = Math.min(variantPage, totalVariantPages - 1);
  const pagedVariants = filteredVariants.slice(safeVariantPage * PAGE_SIZE, safeVariantPage * PAGE_SIZE + PAGE_SIZE);

  const bulk = useBulkSelection(pagedVariants);

  // Reset selection when the visible set changes (filter / search / page).
  useEffect(() => {
    bulk.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variantSearch, variantStatus, variantMissingSku, variantMissingBarcode, variantPage]);

  const bulkSetStatus = async (status: "active" | "paused" | "archived") => {
    setBulkBusy(true);
    setBulkError("");
    let failed = 0;
    for (const vid of bulk.selectedIds) {
      try {
        await productApi.setVariantStatus(orgId, vid, status);
      } catch {
        failed++;
      }
    }
    setBulkBusy(false);
    if (failed > 0) {
      setBulkError(`${failed} of ${bulk.selectedIds.length} variants could not be updated.`);
    }
    bulk.clear();
    invalidate();
  };

  const isSimple = variants.length <= 1;

  return (
    <>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-semibold text-slate-800">{isSimple ? "SKU" : "Variants"}</h3>
            <span className="text-xs text-slate-400">{isSimple ? "1 SKU" : `${variants.length} total`}</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
              <input
                value={variantSearch}
                onChange={(e) => { setVariantSearch(e.target.value); setVariantPage(0); }}
                placeholder="Search SKU, barcode, or option value..."
                className="w-56 pl-8 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
              />
            </div>
            <select
              value={variantStatus}
              onChange={(e) => { setVariantStatus(e.target.value); setVariantPage(0); }}
              className="px-2 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
              aria-label="Filter by status"
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="paused">Paused</option>
              <option value="archived">Archived</option>
            </select>
            <button
              onClick={() => { setVariantMissingSku((v) => !v); setVariantPage(0); }}
              className={`px-2 py-2 text-xs font-medium rounded-lg whitespace-nowrap border ${variantMissingSku ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}
            >
              Missing SKU
            </button>
            <button
              onClick={() => { setVariantMissingBarcode((v) => !v); setVariantPage(0); }}
              className={`px-2 py-2 text-xs font-medium rounded-lg whitespace-nowrap border ${variantMissingBarcode ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}
            >
              Missing barcode
            </button>
            {variants.length > 0 && (
              <button
                type="button"
                onClick={openSkuStandardizer}
                className="px-3 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-medium rounded-lg whitespace-nowrap flex items-center gap-1"
                title="Preview and assign canonical marketplace-safe SKUs"
              >
                <i className="ri-barcode-box-line" /> Standardize SKUs
              </button>
            )}
            {onEditPrices && variants.length > 1 && (
              <button
                type="button"
                onClick={onEditPrices}
                className="px-3 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-medium rounded-lg whitespace-nowrap flex items-center gap-1"
              >
                <i className="ri-price-tag-3-line" /> Edit variant exceptions
              </button>
            )}
            <button
              onClick={() => setBulkOpen(true)}
              className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded-lg whitespace-nowrap flex items-center gap-1"
            >
              <i className="ri-grid-line" /> Bulk add
            </button>
            <button
              onClick={openAddVariant}
              className="px-3 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-medium rounded-lg whitespace-nowrap flex items-center gap-1"
            >
              <i className="ri-add-line" /> Add variant
            </button>
          </div>
        </div>

        {bulk.selectedIds.length > 0 && (
          <div className="px-4 pt-3">
            <BulkActionBar count={bulk.selectedIds.length} onClear={bulk.clear}>
              <button
                onClick={() => bulkSetStatus("active")}
                disabled={bulkBusy}
                className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-xs font-medium rounded-lg whitespace-nowrap"
              >
                Activate
              </button>
              <button
                onClick={() => bulkSetStatus("paused")}
                disabled={bulkBusy}
                className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-60 text-slate-700 text-xs font-medium rounded-lg whitespace-nowrap"
              >
                Pause
              </button>
              <button
                onClick={() => bulkSetStatus("archived")}
                disabled={bulkBusy}
                className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-60 text-slate-700 text-xs font-medium rounded-lg whitespace-nowrap"
              >
                Archive
              </button>
            </BulkActionBar>
            {bulkError && <p className="text-xs text-red-600 mb-2">{bulkError}</p>}
          </div>
        )}

        {variants.length === 0 ? (
          <div className="px-4 py-12 text-center text-sm text-slate-400">
            <i className="ri-grid-line text-2xl text-slate-300 mb-2 block" />
            No variants — use Bulk Add to create multi-dimensional variants
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs text-slate-500">
                  <tr>
                    <th className="px-4 py-3 w-8">
                      <input
                        type="checkbox"
                        checked={bulk.allSelected}
                        onChange={bulk.toggleAll}
                        className="w-4 h-4 rounded accent-emerald-600"
                      />
                    </th>
                    <th className="px-4 py-3 font-medium">Variant</th>
                    <th className="px-4 py-3 font-medium">SKU</th>
                    <th className="px-4 py-3 font-medium">Barcode</th>
                    <th className="px-4 py-3 font-medium">Price</th>
                    <th className="px-4 py-3 font-medium">Cost</th>
                    <th className="px-4 py-3 font-medium">Available</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium w-24"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {pagedVariants.length === 0 ? (
                    <tr><td colSpan={9} className="px-4 py-10 text-center text-sm text-slate-400">No variants match your filters.</td></tr>
                  ) : (
                    pagedVariants.map((v) => {
                      const isArchived = (v.status ?? "active") === "archived";
                      // A loaded empty balance map means no movements: canonical stock is zero.
                      const stock = stockByVariant ? (stockByVariant[v.id] ?? { available: 0, onHand: 0, allocated: 0 }) : undefined;
                      return (
                        <tr key={v.id} className={`hover:bg-slate-50 ${bulk.selected.has(v.id) ? "bg-emerald-50/40" : ""}`}>
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={bulk.selected.has(v.id)}
                              onChange={() => bulk.toggle(v.id)}
                              className="w-4 h-4 rounded accent-emerald-600"
                            />
                          </td>
                          <td className="px-4 py-3 text-slate-700">{variantLabel(v.option_values) || "—"}</td>
                          <td className="px-4 py-3 font-mono text-xs text-slate-700">{v.sku ?? "—"}</td>
                          <td className="px-4 py-3 text-xs text-slate-500 font-mono">{v.barcode ?? "—"}</td>
                          <td className="px-4 py-3 text-slate-600">
                            {(() => {
                              const effective = effectiveSellingPrice(v.selling_price, product.selling_price);
                              if (effective == null) return "—";
                              return (
                                <span>
                                  {money(effective)}
                                  {isVariantPriceOverride(v.selling_price) ? (
                                    <span className="ml-1 text-[10px] tracking-wide text-slate-400">· Override</span>
                                  ) : (
                                    <span className="ml-1 text-[10px] tracking-wide text-slate-400">· Inherited</span>
                                  )}
                                </span>
                              );
                            })()}
                          </td>
                          <td className="px-4 py-3 text-slate-600">
                            {(() => {
                              const effective = effectiveCostPrice(v.cost_price, product.cost_price);
                              if (effective == null) return "—";
                              return (
                                <span>
                                  {money(effective)}
                                  {isVariantCostOverride(v.cost_price) ? (
                                    <span className="ml-1 text-[10px] tracking-wide text-slate-400">· Override</span>
                                  ) : (
                                    <span className="ml-1 text-[10px] tracking-wide text-slate-400">· Inherited</span>
                                  )}
                                </span>
                              );
                            })()}
                          </td>
                          <td className="px-4 py-3">
                            {stock != null ? (
                              <span>
                                <span className={stock.available <= 0 ? "text-red-600 font-medium" : "text-emerald-700 font-medium"}>{stock.available} available</span>
                                <span className="block text-[11px] text-slate-400">{stock.onHand} on hand · {stock.allocated} allocated</span>
                              </span>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3"><StatusBadge status={v.status ?? "active"} /></td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <button onClick={() => openEditVariant(v)} className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700">
                                <i className="ri-pencil-line" />
                              </button>
                              {isArchived ? (
                                <button
                                  onClick={() => restoreVariant(v.id)}
                                  disabled={variantDeleting === v.id}
                                  title="Restore variant"
                                  className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:bg-emerald-50 hover:text-emerald-600"
                                >
                                  <i className="ri-play-circle-line" />
                                </button>
                              ) : (
                                <button
                                  onClick={() => openVariantArchiveConfirm(v.id)}
                                  disabled={variantDeleting === v.id}
                                  title="Archive variant"
                                  className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                                >
                                  <i className="ri-archive-line" />
                                </button>
                              )}
                              <button
                                onClick={() => openVariantDeleteConfirm(v.id)}
                                title="Delete variant permanently"
                                className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500"
                              >
                                <i className="ri-delete-bin-line" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            {totalVariantPages > 1 && (
              <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between flex-wrap gap-3">
                <span className="text-xs text-slate-400">
                  {filteredVariants.length === 0
                    ? "0 variants"
                    : `Showing ${safeVariantPage * PAGE_SIZE + 1}–${safeVariantPage * PAGE_SIZE + pagedVariants.length} of ${filteredVariants.length} variants`}
                </span>
                <div className="flex items-center gap-2">
                  <button onClick={() => setVariantPage((pg) => Math.max(0, pg - 1))} disabled={safeVariantPage === 0} className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-40 text-slate-600 text-xs font-medium rounded-lg whitespace-nowrap">Prev</button>
                  <span className="text-xs text-slate-500">{safeVariantPage + 1} / {totalVariantPages}</span>
                  <button onClick={() => setVariantPage((pg) => Math.min(totalVariantPages - 1, pg + 1))} disabled={safeVariantPage >= totalVariantPages - 1} className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-40 text-slate-600 text-xs font-medium rounded-lg whitespace-nowrap">Next</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <Modal
        open={skuStandardOpen}
        onClose={() => { if (!skuStandardBusy) setSkuStandardOpen(false); }}
        title="Standardize Multichannel SKUs"
        footer={
          <>
            <button
              onClick={() => setSkuStandardOpen(false)}
              disabled={skuStandardBusy}
              className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap"
            >
              Close
            </button>
            <button
              onClick={applySkuStandardization}
              disabled={skuStandardBusy || skuStandardPreview.length === 0 || !!skuValidationError(normalizeSku(skuStandardBase))}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg whitespace-nowrap"
              data-testid="standardize-skus-apply"
            >
              {skuStandardBusy ? "Applying..." : "Apply SKU Standard"}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600">
            One canonical SKU follows the product across eBay, BigCommerce, Amazon, Temu, and future channels.
            SKUs use A-Z, 0-9 and hyphens, are limited to 40 characters, and cannot be renamed after a provider binding exists.
          </div>
          {skuStandardError && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{skuStandardError}</div>}
          {skuStandardSuccess && <div className="px-3 py-2.5 bg-emerald-50 border border-emerald-100 rounded-lg text-sm text-emerald-700">{skuStandardSuccess}</div>}
          <div>
            <label className={labelCls}>Base SKU *</label>
            <input
              value={skuStandardBase}
              onChange={(e) => { setSkuStandardBase(e.target.value); setSkuStandardError(""); setSkuStandardSuccess(""); }}
              className={inputCls}
              placeholder="JUTE450"
              data-testid="standardize-skus-base"
            />
            <p className="text-[11px] text-slate-400 mt-1">
              Use a stable product code, not a price, supplier, warehouse, or marketplace name. Example: JUTE450.
            </p>
            {skuStandardBase && skuValidationError(normalizeSku(skuStandardBase)) && (
              <p className="text-xs text-red-600 mt-1">{skuValidationError(normalizeSku(skuStandardBase))}</p>
            )}
          </div>
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="max-h-80 overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs text-slate-500 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 font-medium">Variant</th>
                    <th className="px-3 py-2 font-medium">Current SKU</th>
                    <th className="px-3 py-2 font-medium">Proposed SKU</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {skuStandardPreview.map(({ variant, oldSku, newSku }) => (
                    <tr key={variant.id}>
                      <td className="px-3 py-2 text-slate-700">{variantLabel(variant.option_values) || "Default"}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-500">{oldSku || "—"}</td>
                      <td className="px-3 py-2 font-mono text-xs text-emerald-700">{newSku}</td>
                    </tr>
                  ))}
                  {skuStandardPreview.length === 0 && (
                    <tr><td colSpan={3} className="px-3 py-8 text-center text-sm text-slate-400">Enter a valid base SKU to preview the changes.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <p className="text-xs text-amber-700">
            Applying this changes catalogue identity and makes affected unpublished listings dirty for review. Provider-bound SKUs are blocked and are never silently renamed.
          </p>
        </div>
      </Modal>

      <Modal
        open={variantOpen}
        onClose={() => setVariantOpen(false)}
        title={variantEditingId ? "Edit Variant" : "Add Variant"}
        footer={
          <>
            <button onClick={() => setVariantOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">
              Cancel
            </button>
            <button onClick={saveVariant} disabled={variantSaving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
              {variantSaving ? "Saving..." : "Save Variant"}
            </button>
          </>
        }
      >
        <form onSubmit={saveVariant} className="space-y-4">
          {variantError && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{variantError}</div>}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>SKU *</label>
              <div className="flex items-center gap-2">
                <input required value={variantForm.sku} onChange={(e) => setVariantForm((f) => ({ ...f, sku: e.target.value }))} className={inputCls} placeholder="WMX-200-BLK" />
                <button
                  type="button"
                  onClick={() => setVariantForm((f) => ({ ...f, sku: generateSku() ?? f.sku }))}
                  className="px-2 py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg text-xs font-medium whitespace-nowrap flex items-center gap-1"
                  title="Generate SKU from product name"
                >
                  <i className="ri-magic-line" />
                </button>
              </div>
            </div>
            <div>
              <label className={labelCls}>Barcode</label>
              <input value={variantForm.barcode} onChange={(e) => setVariantForm((f) => ({ ...f, barcode: e.target.value }))} className={inputCls} placeholder="123456789012" />
            </div>
          </div>
          <div>
            <label className={labelCls}>Option dimensions</label>
            <div className="space-y-2">
              {(variantForm.option_name ? variantForm.option_name.split("|") : []).map((pair, i) => {
                const [name, value] = pair.split(":");
                return (
                  <div key={i} className="grid grid-cols-2 gap-3">
                    <input
                      value={name || ""}
                      onChange={(e) => {
                        const parts = variantForm.option_name.split("|");
                        const newParts = [...parts];
                        const currentVal = parts[i]?.split(":")[1] || "";
                        newParts[i] = `${e.target.value}:${currentVal}`;
                        setVariantForm((f) => ({ ...f, option_name: newParts.join("|") }));
                      }}
                      className={inputCls}
                      placeholder="Option name (e.g. Colour)"
                    />
                    <input
                      value={value || ""}
                      onChange={(e) => {
                        const parts = variantForm.option_name.split("|");
                        const newParts = [...parts];
                        const currentName = parts[i]?.split(":")[0] || "";
                        newParts[i] = `${currentName}:${e.target.value}`;
                        setVariantForm((f) => ({ ...f, option_name: newParts.join("|") }));
                      }}
                      className={inputCls}
                      placeholder="Value (e.g. Black)"
                    />
                  </div>
                );
              })}
              {(!variantForm.option_name || variantForm.option_name === "") && (
                <p className="text-xs text-slate-400">No option dimensions set. Use Bulk Add to create multi-dimensional variants.</p>
              )}
              <button
                type="button"
                onClick={() => {
                  const parts = variantForm.option_name ? variantForm.option_name.split("|") : [];
                  parts.push(":");
                  setVariantForm((f) => ({ ...f, option_name: parts.join("|") }));
                }}
                className="text-xs font-medium text-emerald-600 hover:text-emerald-700 flex items-center gap-1 whitespace-nowrap"
              >
                <i className="ri-add-line" /> Add option dimension
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Cost price</label>
              <input type="number" step="0.01" min="0" value={variantForm.cost_price} onChange={(e) => setVariantForm((f) => ({ ...f, cost_price: e.target.value }))} className={inputCls} placeholder={product.cost_price != null ? `Inherited ${money(product.cost_price)}` : undefined} />
              <p className="text-[11px] text-slate-400 mt-1">Leave blank to inherit the product cost. Enter a value only when this variant has a different cost.</p>
            </div>
            <div>
              <label className={labelCls}>Selling price</label>
              <input type="number" step="0.01" min="0" value={variantForm.selling_price} onChange={(e) => setVariantForm((f) => ({ ...f, selling_price: e.target.value }))} className={inputCls} placeholder={product.selling_price != null ? `Inherited ${product.selling_price}` : "0.00"} />
              <p className="text-[11px] text-slate-400 mt-1">Leave blank to inherit the product selling price. Enter a value only to override it.</p>
            </div>
          </div>
          <div className="pt-2">
            <p className="text-xs font-semibold text-slate-600 mb-2">Shipping defaults</p>
            <div className="grid grid-cols-4 gap-3">
              <div>
                <label className={labelCls}>Weight (kg)</label>
                <input type="number" step="0.01" min="0" value={variantForm.weight_kg} onChange={(e) => setVariantForm((f) => ({ ...f, weight_kg: e.target.value }))} className={inputCls} placeholder={product.weight_kg != null ? `Inherited ${product.weight_kg}` : "0.5"} />
              </div>
              <div>
                <label className={labelCls}>Length (cm)</label>
                <input type="number" step="0.1" min="0" value={variantForm.length_cm} onChange={(e) => setVariantForm((f) => ({ ...f, length_cm: e.target.value }))} className={inputCls} placeholder={product.length_cm != null ? `Inherited ${product.length_cm}` : "20"} />
              </div>
              <div>
                <label className={labelCls}>Width (cm)</label>
                <input type="number" step="0.1" min="0" value={variantForm.width_cm} onChange={(e) => setVariantForm((f) => ({ ...f, width_cm: e.target.value }))} className={inputCls} placeholder={product.width_cm != null ? `Inherited ${product.width_cm}` : "15"} />
              </div>
              <div>
                <label className={labelCls}>Height (cm)</label>
                <input type="number" step="0.1" min="0" value={variantForm.height_cm} onChange={(e) => setVariantForm((f) => ({ ...f, height_cm: e.target.value }))} className={inputCls} placeholder={product.height_cm != null ? `Inherited ${product.height_cm}` : "10"} />
              </div>
            </div>
            <p className="text-[11px] text-slate-400 mt-2">Leave shipping fields blank to inherit the product defaults. Enter a value only when this variant differs.</p>
          </div>
        </form>
      </Modal>

      {bulkOpen && (
        <VariantMatrixModalV2
          productId={productId}
          orgId={orgId}
          productSku={product.reference ?? product.sku}
          productName={product.name}
          defaultCost={product.cost_price}
          defaultSelling={product.selling_price}
          defaultWeightKg={product.weight_kg}
          defaultLengthCm={product.length_cm}
          defaultWidthCm={product.width_cm}
          defaultHeightCm={product.height_cm}
          reorderPoint={product.reorder_point}
          reorderQty={product.reorder_qty}
          existingCount={variants.length}
          existingSkus={variants.map((v) => v.sku).filter((s): s is string => !!s)}
          existingVariants={variants.map((v) => ({ id: v.id, sku: v.sku, optionValues: v.option_values }))}
          onClose={() => setBulkOpen(false)}
          onComplete={invalidate}
        />
      )}

      <Modal
        open={variantArchiveId !== null}
        onClose={() => setVariantArchiveId(null)}
        title="Archive Variant"
        footer={
          <>
            <button onClick={() => setVariantArchiveId(null)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Cancel</button>
            <button
              onClick={() => { if (variantArchiveId) archiveVariant(variantArchiveId); }}
              disabled={variantArchiveLoading || !variantArchiveReadiness?.can_archive}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-white text-sm font-medium rounded-lg whitespace-nowrap"
            >
              Archive
            </button>
          </>
        }
      >
        <div className="space-y-4">
          {variantArchiveError && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{variantArchiveError}</div>}
          {variantArchiveLoading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-2"><i className="ri-loader-4-line animate-spin" /> Checking operational references...</div>
          ) : variantArchiveReadiness ? (
            <>
              {variantArchiveReadiness.blockers.length > 0 ? (
                <div>
                  <p className="text-sm font-medium text-slate-800 mb-2">This variant cannot be archived yet:</p>
                  <ul className="space-y-1.5">
                    {variantArchiveReadiness.blockers.map((b) => (
                      <li key={b.code} className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                        <i className="ri-error-warning-line mt-0.5" /> {b.message}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="px-3 py-2.5 bg-emerald-50 border border-emerald-100 rounded-lg text-sm text-emerald-700">
                  <i className="ri-check-line mr-1" /> No blocking references. Safe to archive.
                </div>
              )}
              {variantArchiveReadiness.warnings.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-slate-800 mb-2">Please note:</p>
                  <ul className="space-y-1.5">
                    {variantArchiveReadiness.warnings.map((w) => (
                      <li key={w.code} className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                        <i className="ri-alert-line mt-0.5" /> {w.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          ) : null}
        </div>
      </Modal>

      <Modal
        open={variantDeleteId !== null}
        onClose={() => setVariantDeleteId(null)}
        title="Delete Variant Permanently"
        footer={
          <>
            <button onClick={() => setVariantDeleteId(null)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Cancel</button>
            <button
              onClick={handleVariantDelete}
              disabled={
                variantDeleteBusy
                || variantDeleteLoading
                || !variantDeleteSafety?.can_delete
                || (variantDeleteSafety.requires_stock_disposition && (!variantWriteOffStock || !variantDeleteSafety.stock_confirmation))
              }
              className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg whitespace-nowrap"
            >
              {variantDeleteBusy ? "Deleting..." : "Delete permanently"}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          {variantDeleteError && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{variantDeleteError}</div>}
          <p className="text-sm text-slate-600">
            This removes the live variant. Completed sales and historical stock movements keep their own snapshots and do not require the variant to remain in the catalogue.
          </p>
          {variantDeleteLoading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-2"><i className="ri-loader-4-line animate-spin" /> Checking live operational dependencies...</div>
          ) : variantDeleteSafety ? (
            <>
              {variantDeleteSafety.blockers.length > 0 ? (
                <div>
                  <p className="text-sm font-medium text-slate-800 mb-2">This variant cannot be deleted yet:</p>
                  <ul className="space-y-1.5">
                    {variantDeleteSafety.blockers.map((b) => (
                      <li key={b.code} className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                        <i className="ri-error-warning-line mt-0.5" /> {b.message}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="px-3 py-2.5 bg-emerald-50 border border-emerald-100 rounded-lg text-sm text-emerald-700">
                  <i className="ri-check-line mr-1" /> No unresolved operational dependencies.
                </div>
              )}

              {variantDeleteSafety.warnings.length > 0 && (
                <ul className="space-y-1.5">
                  {variantDeleteSafety.warnings.map((w) => (
                    <li key={w.code} className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                      <i className="ri-alert-line mt-0.5" /> {w.message}
                    </li>
                  ))}
                </ul>
              )}

              {variantDeleteSafety.requires_stock_disposition && <StockDispositionPreview snapshot={variantDeleteSafety.stock_confirmation} />}
              {variantDeleteSafety.requires_stock_disposition && (
                <label className="flex items-start gap-2 text-sm text-slate-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <input
                    type="checkbox"
                    checked={variantWriteOffStock}
                    onChange={(e) => setVariantWriteOffStock(e.target.checked)}
                    className="mt-1"
                  />
                  <span>
                    Write off <strong>{variantDeleteSafety.on_hand}</strong> unit(s) from active inventory through the inventory ledger, then delete this variant.
                  </span>
                </label>
              )}

              <p className="text-xs text-slate-500">
                Historical sales preserved: {variantDeleteSafety.historical_sales}. Historical stock movements preserved: {variantDeleteSafety.historical_movements}.
              </p>
            </>
          ) : null}
        </div>
      </Modal>
    </>
  );
}